import { useEffect, useState, useRef, useCallback } from 'react';
import { Promotion, UserActivatedPromotion, UserTriggeredPromotion } from '@lunit/shared-types';
import { getPromotions, activatePromoCode, activatePromotion, getActivatedPromotions, getTriggeredPromotions, trackImpressions } from '@core/api/promotions';
import { useVenue } from '@core/contexts/VenueContext';
import { useUser } from '@core/contexts/ProfileContext';
import { Card } from '@core/components/Card';
import { Skeleton } from '@core/components/Skeleton';
import { Calendar, Check, Clock, Crown, Layers, Package, Repeat, ShoppingCart, Sparkles, Tag, TrendingUp, Users, X, Ticket, Gift, Timer, Star, Cake, UserPlus, Zap, Loader2 } from 'lucide-react';
import { Header } from '@core/components/Header';

// Telegram HapticFeedback helper (types from vite-env.d.ts)
const haptic = {
  success: () => window.Telegram?.WebApp.HapticFeedback.notificationOccurred('success'),
  error: () => window.Telegram?.WebApp.HapticFeedback.notificationOccurred('error'),
  impact: () => window.Telegram?.WebApp.HapticFeedback.impactOccurred('medium'),
};

// День недели (0=Вс, 1=Пн, ... 6=Сб)
const DAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

// Получить текст расписания
function getScheduleText(days: number[], startTime?: string, endTime?: string): string {
  if (days.length === 0) return '';

  const sorted = [...days].sort((a, b) => a - b);
  let daysText: string;

  if (sorted.length === 7) {
    daysText = 'Каждый день';
  } else if (JSON.stringify(sorted) === JSON.stringify([1, 2, 3, 4, 5])) {
    daysText = 'Пн-Пт';
  } else if (JSON.stringify(sorted) === JSON.stringify([0, 6])) {
    daysText = 'Сб-Вс';
  } else {
    daysText = sorted.map(d => DAY_NAMES[d]).join(', ');
  }

  if (startTime && endTime) {
    return `${daysText}, ${startTime}–${endTime}`;
  }
  return daysText;
}

// Проверить, активна ли акция прямо сейчас
function isActiveNow(promotion: Promotion): boolean {
  if (!promotion.isRecurring || !promotion.recurringDays?.length) {
    return true; // Не рекуррентная - всегда активна в период действия
  }

  const now = new Date();
  const currentDay = now.getDay();

  // Проверяем день недели
  if (!promotion.recurringDays.includes(currentDay)) {
    return false;
  }

  // Проверяем время
  if (promotion.recurringStartTime && promotion.recurringEndTime) {
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Обработка ночных диапазонов (например 22:00-02:00)
    if (promotion.recurringStartTime > promotion.recurringEndTime) {
      return currentTime >= promotion.recurringStartTime || currentTime <= promotion.recurringEndTime;
    }

    return currentTime >= promotion.recurringStartTime && currentTime <= promotion.recurringEndTime;
  }

  return true;
}

// Получить текст ограничения по уровням
function getTierTargetingText(targetTiers: string[]): string | null {
  if (!targetTiers || targetTiers.length === 0) return null;

  // Сортируем по порядку уровней
  const tierOrder = ['Bronze', 'Silver', 'Gold', 'Platinum'];
  const sorted = [...targetTiers].sort((a, b) => tierOrder.indexOf(a) - tierOrder.indexOf(b));

  // Если Gold+ (Gold и Platinum)
  if (sorted.length === 2 && sorted.includes('Gold') && sorted.includes('Platinum')) {
    return 'Только для Gold+';
  }

  // Если один уровень
  if (sorted.length === 1) {
    return `Только для ${sorted[0]}`;
  }

  return `Для ${sorted.join(', ')}`;
}

// Проверить, доступна ли акция для уровня пользователя
function isAvailableForTier(promotion: Promotion, userTier: string): boolean {
  let tiers = promotion.targetTiers;

  // DEBUG: Log exact values and types
  console.log(`[TierCheck] "${promotion.title}"`, {
    targetTiers: tiers,
    targetTiersType: typeof tiers,
    isArray: Array.isArray(tiers),
    length: tiers?.length,
    userTier,
    userTierType: typeof userTier,
    rawValue: JSON.stringify(tiers)
  });

  // FIX: Handle case where PostgreSQL returns array as string "{value1,value2}"
  if (typeof tiers === 'string') {
    console.log(`[TierCheck] WARNING: targetTiers came as string, parsing...`);
    // Parse PostgreSQL array format: "{Bronze,Silver}" → ["Bronze", "Silver"]
    const parsed = (tiers as string).replace(/^\{|\}$/g, '').split(',').filter(Boolean);
    tiers = parsed;
  }

  // Пустой массив = доступно всем
  if (!tiers || tiers.length === 0) {
    console.log(`[TierCheck] "${promotion.title}" → PASS (no tier restriction)`);
    return true;
  }

  // Case-insensitive comparison
  const userTierLower = userTier.toLowerCase();
  const result = tiers.some(t => t.toLowerCase() === userTierLower);

  console.log(`[TierCheck] "${promotion.title}" → ${result ? 'PASS' : 'FAIL'} (tiers=${JSON.stringify(tiers)}, user=${userTier})`);
  return result;
}

// Получить текст для SKU targeting
function getSkuTargetingText(promotion: Promotion): string | null {
  if (!promotion.skuTargetType || promotion.skuTargetType === 'ALL') {
    // Check for exclusions
    if (promotion.excludedCategoryNames && promotion.excludedCategoryNames.length > 0) {
      return `На все товары, кроме: ${promotion.excludedCategoryNames.join(', ')}`;
    }
    return null;
  }

  if (promotion.skuTargetType === 'CATEGORIES') {
    if (promotion.includedCategoryNames && promotion.includedCategoryNames.length > 0) {
      let text = `На ${promotion.includedCategoryNames.join(', ')}`;
      if (promotion.excludedCategoryNames && promotion.excludedCategoryNames.length > 0) {
        text += ` (кроме: ${promotion.excludedCategoryNames.join(', ')})`;
      }
      return text;
    }
  }

  if (promotion.skuTargetType === 'ITEMS') {
    if (promotion.includedItemNames && promotion.includedItemNames.length > 0) {
      // Show up to 3 items, then "и ещё X"
      const items = promotion.includedItemNames;
      if (items.length <= 3) {
        return `На: ${items.join(', ')}`;
      }
      return `На: ${items.slice(0, 3).join(', ')} и ещё ${items.length - 3}`;
    }
  }

  return null;
}

export function PromotionsPage() {
  const { venue, features } = useVenue();
  const { user } = useUser();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [activatedPromotions, setActivatedPromotions] = useState<UserActivatedPromotion[]>([]);
  const [triggeredPromotions, setTriggeredPromotions] = useState<UserTriggeredPromotion[]>([]);
  const [loading, setLoading] = useState(true);

  // Promo code modal
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);

  // OPT_IN activation state
  const [activatingPromotionId, setActivatingPromotionId] = useState<string | null>(null);
  const [activationMessage, setActivationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Уровень пользователя (по умолчанию Bronze)
  const userTier = user?.tier || 'Bronze';

  // DEBUG: Log user tier info on every render
  console.log('[UserTier] Current user tier:', {
    rawTier: user?.tier,
    finalTier: userTier,
    userId: user?.id,
    userName: user?.firstName
  });

  useEffect(() => {
    if (venue && features?.promotionsPublished) {
      void loadPromotions();
      void loadActivatedPromotions();
      void loadTriggeredPromotions();
    } else if (venue && features !== null && !features?.promotionsPublished) {
      // Promotions not published - stop loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.id, features?.promotionsPublished, user?.id]);

  // Track which promotions have been tracked for impressions
  const trackedImpressionsRef = useRef<Set<string>>(new Set());

  const loadPromotions = async () => {
    try {
      if (!venue) {
        return;
      }
      // Pass userId to get user's usage data for each promotion
      // activationType=AUTO is default in the API now
      const data = await getPromotions(venue.id, {
        isActive: true,
        userId: user?.id
      });

      // DEBUG: Log all promotions from API
      console.log('[Promotions] API returned:', data.length, 'promotions');
      console.log('[Promotions] User tier:', user?.tier);
      data.forEach(p => {
        console.log(`[Promotion] "${p.title}" | type=${p.activationType} | tiers=${JSON.stringify(p.targetTiers)} | active=${p.isActive}`);
      });

      setPromotions(data);

      // Track impressions for loaded promotions (only once per session)
      const newPromotionIds = data
        .filter(p => !trackedImpressionsRef.current.has(p.id))
        .map(p => p.id);

      if (newPromotionIds.length > 0) {
        // Mark as tracked before API call to prevent duplicates
        newPromotionIds.forEach(id => trackedImpressionsRef.current.add(id));
        // Fire and forget - don't await to not block UI
        trackImpressions(newPromotionIds, venue.id, user?.id).catch(err => {
          console.error('Failed to track impressions:', err);
        });
      }
    } catch (error) {
      console.error('Failed to load promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivatedPromotions = async () => {
    try {
      if (!venue || !user?.id) {
        return;
      }
      const data = await getActivatedPromotions(user.id, venue.id);
      setActivatedPromotions(data);
    } catch (error) {
      console.error('Failed to load activated promotions:', error);
    }
  };

  const loadTriggeredPromotions = async () => {
    try {
      if (!venue || !user?.id) {
        return;
      }
      const data = await getTriggeredPromotions(user.id, venue.id, 'ACTIVE');
      setTriggeredPromotions(data);
    } catch (error) {
      console.error('Failed to load triggered promotions:', error);
    }
  };

  const handleActivatePromoCode = async () => {
    if (!promoCode.trim() || !venue || !user?.id) {
      return;
    }

    setPromoLoading(true);
    setPromoError(null);
    setPromoSuccess(null);

    try {
      const result = await activatePromoCode(promoCode.trim(), user.id, venue.id);

      if (result.success) {
        setPromoSuccess(result.message);
        setPromoCode('');
        // Reload activated promotions
        void loadActivatedPromotions();
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowPromoModal(false);
          setPromoSuccess(null);
        }, 2000);
      } else {
        setPromoError(result.message);
      }
    } catch (error) {
      setPromoError('Ошибка активации промокода');
    } finally {
      setPromoLoading(false);
    }
  };

  // Handle OPT_IN promotion activation
  const handleActivatePromotion = useCallback(async (promotionId: string) => {
    if (!user?.id) {
      return;
    }

    setActivatingPromotionId(promotionId);
    setActivationMessage(null);

    try {
      const result = await activatePromotion(promotionId, user.id);

      if (result.success) {
        haptic.success();
        setActivationMessage({ type: 'success', text: result.message || 'Акция активирована!' });
        // Reload promotions to get updated activation status
        void loadPromotions();
        void loadActivatedPromotions();
        // Clear success message after 3 seconds
        setTimeout(() => {
          setActivationMessage(null);
        }, 3000);
      } else {
        haptic.error();
        setActivationMessage({ type: 'error', text: result.message || 'Не удалось активировать акцию' });
      }
    } catch (error) {
      haptic.error();
      setActivationMessage({ type: 'error', text: 'Ошибка сети. Попробуйте ещё раз.' });
    } finally {
      setActivatingPromotionId(null);
    }
  }, [user?.id]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-48" />
        ))}
      </div>
    );
  }

  // Фильтруем акции:
  // 1. По уровню пользователя
  // 2. Исключаем PROMO_CODE (они показываются в разделе "Активные акции" после ввода)
  const afterTierFilter = promotions.filter(p => isAvailableForTier(p, userTier));
  const afterTypeFilter = afterTierFilter.filter(p => p.activationType !== 'PROMO_CODE');

  // DEBUG: Log filtering results
  console.log('[Filter] After tier filter:', afterTierFilter.length, '/', promotions.length);
  console.log('[Filter] After type filter:', afterTypeFilter.length, '/', afterTierFilter.length);
  promotions.forEach(p => {
    const passesTier = isAvailableForTier(p, userTier);
    const passesType = p.activationType !== 'PROMO_CODE';
    if (!passesTier || !passesType) {
      console.log(`[Filtered OUT] "${p.title}" | tier=${passesTier} | type=${passesType} (activationType=${p.activationType})`);
    }
  });

  const availablePromotions = afterTypeFilter
    // Сортировка: OPT_IN не активированные первыми, затем активированные, потом AUTO
    .sort((a, b) => {
      const aIsOptInNotActivated = a.activationType === 'OPT_IN' && !a.userActivation?.isActivated;
      const bIsOptInNotActivated = b.activationType === 'OPT_IN' && !b.userActivation?.isActivated;

      if (aIsOptInNotActivated && !bIsOptInNotActivated) return -1;
      if (!aIsOptInNotActivated && bIsOptInNotActivated) return 1;
      return 0;
    });

  const featuredPromotions = availablePromotions.filter((promotion) => promotion.isFeatured);
  const regularPromotions = availablePromotions.filter((promotion) => !promotion.isFeatured);

  // Helper to format time remaining
  const formatTimeRemaining = (expiresAt: string): string => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return 'Истёк';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} дн.`;
    }
    if (hours > 0) {
      return `${hours} ч. ${minutes} мин.`;
    }
    return `${minutes} мин.`;
  };

  return (
    <div className="pb-20">
      <Header showBack={true} />

      <div className="p-4 space-y-6 -mt-4">
        {/* Promo Code Entry Button */}
        <button
          onClick={() => {
            setShowPromoModal(true);
            setPromoCode('');
            setPromoError(null);
            setPromoSuccess(null);
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-2xl font-medium shadow-lg shadow-purple-500/20 active:scale-[0.98] transition-transform"
        >
          <Ticket className="w-5 h-5" />
          Ввести промокод
        </button>

        {/* Activated Promotions Section */}
        {activatedPromotions.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-text-main flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              Активные акции
            </h2>
            <div className="space-y-3">
              {activatedPromotions.map((activation) => (
                <div
                  key={activation.id}
                  className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 shadow-lg shadow-purple-500/25"
                >
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />

                  <div className="relative flex items-start gap-3">
                    {/* Icon in semi-transparent circle */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Star className="w-6 h-6 text-white fill-white/80" />
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      <h3 className="font-bold text-white text-base leading-tight">
                        {activation.promotion.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-white/85 mt-1 line-clamp-2 leading-snug">
                        {activation.promotion.description}
                      </p>

                      {/* Timer */}
                      {activation.expiresAt && (
                        <div className="flex items-center gap-1.5 mt-3 text-xs text-white/90">
                          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/15 backdrop-blur-sm">
                            <Clock className="w-3.5 h-3.5" />
                            <span>Действует ещё: {formatTimeRemaining(activation.expiresAt)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Offers (Triggered Promotions) Section */}
        {triggeredPromotions.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-text-main flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Персональные предложения
            </h2>
            <div className="space-y-3">
              {triggeredPromotions.map((triggered) => (
                <Card key={triggered.id} className="border-2 border-yellow-200 bg-yellow-50/50">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center">
                      {triggered.promotion.triggerType === 'BIRTHDAY' ? (
                        <Cake className="w-5 h-5" />
                      ) : triggered.promotion.triggerType === 'REGISTRATION' ? (
                        <UserPlus className="w-5 h-5" />
                      ) : triggered.promotion.triggerType === 'FIRST_PURCHASE' ? (
                        <Gift className="w-5 h-5" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-text-main truncate">
                          {triggered.promotion.title}
                        </h3>
                        <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                          Для вас
                        </span>
                      </div>
                      <p className="text-sm text-text-muted line-clamp-2">
                        {triggered.promotion.description}
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
                        <Timer className="w-3.5 h-3.5" />
                        Действует ещё: {formatTimeRemaining(triggered.expiresAt)}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {featuredPromotions.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-text-main flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Горячие предложения
            </h2>
            <div className="space-y-3">
              {featuredPromotions.map((promotion) => (
                <PromotionCard
                  key={promotion.id}
                  promotion={promotion}
                  featured
                  onActivate={handleActivatePromotion}
                  isActivating={activatingPromotionId === promotion.id}
                />
              ))}
            </div>
          </div>
        )}

        {regularPromotions.length > 0 && (
          <div>
            {(featuredPromotions.length > 0 || activatedPromotions.length > 0) && (
              <h2 className="text-lg font-semibold mb-3 text-text-main">Все акции</h2>
            )}
            <div className="space-y-3">
              {regularPromotions.map((promotion) => (
                <PromotionCard
                  key={promotion.id}
                  promotion={promotion}
                  onActivate={handleActivatePromotion}
                  isActivating={activatingPromotionId === promotion.id}
                />
              ))}
            </div>
          </div>
        )}

        {promotions.length === 0 && activatedPromotions.length === 0 && triggeredPromotions.length === 0 && (
          <Card className="text-center py-12">
            <Tag className="w-12 h-12 mx-auto mb-3 text-text-main opacity-30" />
            <h3 className="font-semibold mb-2 text-text-main">Нет активных акций</h3>
            <p className="text-sm text-text-main opacity-60">
              Возвращайтесь позже — скоро появятся новые предложения!
            </p>
          </Card>
        )}
      </div>

      {/* Activation Message Toast */}
      {activationMessage && (
        <div className={`fixed top-4 left-4 right-4 z-50 p-4 rounded-2xl shadow-lg animate-in slide-in-from-top ${
          activationMessage.type === 'success'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-3">
            {activationMessage.type === 'success' ? (
              <Check className="w-5 h-5 flex-shrink-0" />
            ) : (
              <X className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{activationMessage.text}</p>
          </div>
        </div>
      )}

      {/* Promo Code Modal - Dark Theme */}
      {showPromoModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4">
          <div className="bg-[#1a1a1a] rounded-t-3xl rounded-b-xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Ввести промокод</h2>
              <button
                onClick={() => setShowPromoModal(false)}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {promoSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center">
                    <Check className="w-8 h-8" />
                  </div>
                  <p className="text-lg font-semibold text-white">{promoSuccess}</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Введите код
                    </label>
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoError(null);
                      }}
                      placeholder="Например: COFFEE2024"
                      className="w-full px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/40 text-center text-lg font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoFocus
                      maxLength={20}
                    />
                    {promoError && (
                      <p className="mt-2 text-sm text-red-400 text-center">{promoError}</p>
                    )}
                  </div>

                  <button
                    onClick={handleActivatePromoCode}
                    disabled={promoLoading || !promoCode.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
                  >
                    {promoLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Ticket className="w-5 h-5" />
                        Активировать
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PromotionCard({
  promotion,
  featured,
  onActivate,
  isActivating
}: {
  promotion: Promotion;
  featured?: boolean;
  onActivate?: (promotionId: string) => void;
  isActivating?: boolean;
}) {
  const icon = (() => {
    switch (promotion.type) {
      case 'DISCOUNT':
        return <Tag className="w-5 h-5" />;
      case 'BONUS_POINTS':
        return <TrendingUp className="w-5 h-5" />;
      case 'FREE_ITEM':
        return <Sparkles className="w-5 h-5" />;
      case 'BUY_X_GET_Y':
        return <Tag className="w-5 h-5" />;
      default:
        return <Tag className="w-5 h-5" />;
    }
  })();

  const valueText = (() => {
    switch (promotion.type) {
      case 'DISCOUNT':
        if (promotion.discountType === 'PERCENTAGE') {
          return `−${promotion.discountValue}%`;
        }
        return `−${promotion.discountValue}₽`;
      case 'BONUS_POINTS':
        // If discountValue > 1, it's a multiplier
        if (promotion.discountValue && promotion.discountValue > 1) {
          return `x${promotion.discountValue}`;
        }
        return `+${promotion.bonusPoints || 0}`;
      case 'FREE_ITEM':
        return 'Подарок';
      case 'BUY_X_GET_Y':
        return `${promotion.buyQuantity}+${promotion.getQuantity}`;
      default:
        return 'Акция';
    }
  })();

  // Detailed description of the reward
  const rewardDescription = (() => {
    switch (promotion.type) {
      case 'DISCOUNT':
        if (promotion.discountType === 'PERCENTAGE') {
          return `Скидка ${promotion.discountValue}%`;
        }
        return `Скидка ${promotion.discountValue}₽`;
      case 'BONUS_POINTS':
        if (promotion.discountValue && promotion.discountValue > 1) {
          return `Баллы x${promotion.discountValue}`;
        }
        return `+${promotion.bonusPoints || 0} бонусных баллов`;
      case 'FREE_ITEM':
        return 'Товар в подарок';
      case 'BUY_X_GET_Y':
        return `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity}`;
      default:
        return '';
    }
  })();

  const endDate = new Date(promotion.endDate);
  const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  // Happy Hours info
  const isRecurring = promotion.isRecurring && promotion.recurringDays && promotion.recurringDays.length > 0;
  const scheduleText = isRecurring
    ? getScheduleText(promotion.recurringDays!, promotion.recurringStartTime, promotion.recurringEndTime)
    : null;
  const activeNow = isRecurring ? isActiveNow(promotion) : true;

  return (
    <Card className={featured ? 'border-2 border-brand-accent' : ''}>
      {promotion.imageUrl && (
        <div className="relative -m-4 mb-4 h-40 overflow-hidden rounded-t-brand">
          <img src={promotion.imageUrl} alt={promotion.title} className="w-full h-full object-cover" />
          <div className="absolute top-3 left-3 bg-brand-button text-brand-button-text px-3 py-1.5 rounded-full font-bold text-lg">
            {valueText}
          </div>
          {featured && (
            <div className="absolute top-3 right-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
              <Sparkles className="w-4 h-4" />
              Хит
            </div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-bold text-lg text-text-main flex items-center gap-2">
            {icon}
            {promotion.title}
          </h3>
          {promotion.isExclusive && (
            <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
              Эксклюзивно
            </span>
          )}
        </div>
        <p className="text-sm text-text-main opacity-70 mb-2">{promotion.description}</p>

        {/* Reward description badge */}
        {rewardDescription && (
          <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
            {rewardDescription}
          </div>
        )}

        {/* Happy Hours Schedule */}
        {isRecurring && scheduleText && (
          <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${
            activeNow
              ? 'bg-green-500/10 text-green-600'
              : 'bg-gray-500/10 text-text-muted'
          }`}>
            <Clock className="w-4 h-4 flex-shrink-0" />
            <div className="text-sm">
              <span className="font-medium">
                {activeNow ? 'Действует сейчас: ' : 'Расписание: '}
              </span>
              <span>{scheduleText}</span>
              {!activeNow && (
                <span className="ml-2 text-xs opacity-70">(сейчас не активна)</span>
              )}
            </div>
          </div>
        )}

        {/* Minimum purchase amount */}
        {promotion.minPurchaseAmount && promotion.minPurchaseAmount > 0 && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-blue-500/10 text-blue-600">
            <ShoppingCart className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">
              При заказе от <span className="font-semibold">{promotion.minPurchaseAmount}₽</span>
            </span>
          </div>
        )}

        {/* Global limit - remaining spots */}
        {promotion.maxUsesTotal && promotion.maxUsesTotal > 0 && (
          <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${
            promotion.usesCount >= promotion.maxUsesTotal
              ? 'bg-red-500/10 text-red-600'
              : promotion.usesCount >= promotion.maxUsesTotal * 0.8
                ? 'bg-orange-500/10 text-orange-600'
                : 'bg-purple-500/10 text-purple-600'
          }`}>
            <Users className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm">
                {promotion.usesCount >= promotion.maxUsesTotal ? (
                  <span className="font-semibold">Лимит исчерпан</span>
                ) : (
                  <span>
                    Осталось: <span className="font-semibold">{promotion.maxUsesTotal - promotion.usesCount}</span> мест
                  </span>
                )}
              </div>
              {/* Progress bar */}
              <div className="mt-1.5 h-1.5 bg-white/30 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    promotion.usesCount >= promotion.maxUsesTotal
                      ? 'bg-red-500'
                      : promotion.usesCount >= promotion.maxUsesTotal * 0.8
                        ? 'bg-orange-500'
                        : 'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(100, (promotion.usesCount / promotion.maxUsesTotal) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tier targeting badge */}
        {getTierTargetingText(promotion.targetTiers || []) && (
          <div className="flex items-center gap-1.5 mb-2 text-xs text-amber-600">
            <Crown className="w-3.5 h-3.5" />
            <span>{getTierTargetingText(promotion.targetTiers || [])}</span>
          </div>
        )}

        {/* SKU targeting badge */}
        {getSkuTargetingText(promotion) && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-indigo-500/10 text-indigo-600">
            {promotion.skuTargetType === 'ITEMS' ? (
              <Package className="w-4 h-4 flex-shrink-0" />
            ) : (
              <Layers className="w-4 h-4 flex-shrink-0" />
            )}
            <span className="text-sm">{getSkuTargetingText(promotion)}</span>
          </div>
        )}

        {/* Usage status - only show when there's meaningful limit info */}
        {/* Skip for AUTO/OPT_IN when just showing "Доступно" - activation section handles that */}
        {promotion.userUsage && promotion.userLimitType !== 'UNLIMITED' && (
          // Only show if: has remaining count, OR can't use (limit reached)
          (promotion.userUsage.remaining !== null || !promotion.userUsage.canUse) && (
          <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${
            promotion.userUsage.canUse
              ? 'bg-green-500/10 text-green-600'
              : 'bg-orange-500/10 text-orange-600'
          }`}>
            {promotion.userUsage.canUse ? (
              <Check className="w-4 h-4 flex-shrink-0" />
            ) : (
              <X className="w-4 h-4 flex-shrink-0" />
            )}
            <div className="text-sm">
              {promotion.userUsage.canUse ? (
                <span>Осталось: <span className="font-semibold">{promotion.userUsage.remaining}</span> {promotion.userLimitType === 'PER_DAY' ? 'сегодня' : ''}</span>
              ) : (
                promotion.userLimitType === 'PER_DAY' ? (
                  <span className="flex items-center gap-1">
                    <Repeat className="w-3.5 h-3.5" />
                    Доступно завтра
                  </span>
                ) : (
                  <span>Лимит исчерпан</span>
                )
              )}
            </div>
          </div>
        ))}

        {/* Activation Status Section */}
        {(() => {
          const activationType = promotion.activationType || 'AUTO';
          const userActivation = promotion.userActivation;

          // Check if OPT_IN activation limit is reached
          const isOptInLimitReached = activationType === 'OPT_IN' &&
            promotion.maxActivations !== undefined &&
            promotion.maxActivations !== null &&
            (promotion.activationsCount ?? 0) >= promotion.maxActivations;

          // Check if activation has expired
          const isActivationExpired = userActivation?.expiresAt &&
            new Date(userActivation.expiresAt) < new Date();

          // AUTO type
          if (activationType === 'AUTO') {
            return (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-green-500/10 text-green-600">
                <Check className="w-4 h-4 flex-shrink-0" />
                <div className="text-sm">
                  <span className="font-medium">Применяется автоматически</span>
                  <p className="text-xs opacity-70 mt-0.5">Бонус применится при следующей покупке</p>
                </div>
              </div>
            );
          }

          // OPT_IN type
          if (activationType === 'OPT_IN') {
            // Already activated
            if (userActivation?.isActivated) {
              // Check if expired
              if (isActivationExpired) {
                return (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-gray-500/10 text-gray-500">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">Срок действия истёк</span>
                  </div>
                );
              }

              // Check if already used
              if (userActivation.isUsed) {
                return (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-gray-500/10 text-gray-500">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">Уже использовано</span>
                  </div>
                );
              }

              // Active and ready to use
              return (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-green-500/10 text-green-600">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="font-medium">Активировано</span>
                    {userActivation.expiresAt && (
                      <p className="text-xs opacity-70 mt-0.5">
                        Действует до: {new Date(userActivation.expiresAt).toLocaleDateString('ru-RU', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    )}
                    <p className="text-xs opacity-70 mt-0.5">Бонус применится при следующей покупке</p>
                  </div>
                </div>
              );
            }

            // Activation limit reached
            if (isOptInLimitReached) {
              return (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-red-500/10 text-red-600">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">Акция закончилась</span>
                </div>
              );
            }

            // Not activated - show button
            return (
              <button
                onClick={() => onActivate?.(promotion.id)}
                disabled={isActivating}
                className="w-full flex items-center justify-center gap-2 mb-3 px-4 py-3 bg-primary text-white rounded-xl font-medium active:scale-[0.98] transition-transform disabled:opacity-70 disabled:active:scale-100"
              >
                {isActivating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Активация...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Активировать
                  </>
                )}
              </button>
            );
          }

          return null;
        })()}

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5 text-text-main opacity-60">
            <Calendar className="w-4 h-4" />
            <span>
              {daysLeft > 0
                ? `Осталось ${daysLeft} ${daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дня' : 'дней'}`
                : 'Последний день!'}
            </span>
          </div>
          {/* Show limit type if no userUsage data */}
          {!promotion.userUsage && promotion.maxUsesPerUser && (
            <div className="text-xs bg-card px-2 py-1 rounded text-text-main">
              Лимит: {promotion.maxUsesPerUser}x{promotion.userLimitType === 'PER_DAY' ? '/день' : ''}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

